import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Crown, Car, ArrowRight, ReceiptText, CalendarRange, History } from "lucide-react";

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const todayStr = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLE = {
  confirmed: "text-emerald-700 border-emerald-200 bg-emerald-50",
  completed: "text-sky-700 border-sky-200 bg-sky-50",
  cancelled: "text-red-700 border-red-200 bg-red-50",
  pending_payment: "text-amber-700 border-amber-200 bg-amber-50",
};

export default function MyRentals() {
  useSEO({ title: "My Rentals", description: "Your Easy Drive trips, receipts and loyalty progress.", path: "/my-rentals", noindex: true });
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(sp.get("email") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (em) => {
    setLoading(true);
    try {
      const { data } = await api.get("/my-rentals", { params: { email: (em ?? email).trim() } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load your rentals");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (sp.get("email")) load(sp.get("email")); }, []); // eslint-disable-line

  const bookings = data?.bookings || [];
  const loyalty = data?.loyalty;
  const upcoming = bookings.filter((b) => b.status === "confirmed" && b.end_date >= todayStr());
  const past = bookings.filter((b) => !upcoming.includes(b));
  const progressPct = loyalty?.next_tier
    ? Math.min(100, Math.round(((loyalty.rentals_to_next > 0 ? (loyalty.rentals - (loyalty.rentals - 0) + loyalty.rentals) : loyalty.rentals) / (loyalty.rentals + loyalty.rentals_to_next)) * 100))
    : 100;

  const RentalRow = ({ b, i, past: isPast }) => (
    <div data-testid={`rental-row-${i}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
      <div>
        <p className="font-display text-lg tracking-tight flex items-center gap-2"><Car size={15} className="gold-text" /> {b.vehicle_name}</p>
        <p className="text-xs font-ui text-muted-foreground mt-0.5">{b.booking_ref} · {fmtDate(b.start_date)} → {fmtDate(b.end_date)} · {fmtMoney(b.total)}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] font-ui font-semibold uppercase tracking-wide border rounded-full px-2.5 py-1 ${STATUS_STYLE[b.status] || STATUS_STYLE.pending_payment}`}>{(b.status || "").replace("_", " ")}</span>
        {b.status === "confirmed" && (
          <Link to={`/extend/${b.id}`} onClick={() => localStorage.setItem(`booking_email_${b.id}`, email.trim().toLowerCase())}>
            <Button data-testid={`rental-extend-${i}`} size="sm" variant="outline" className="rounded-full font-ui h-8 text-xs">Extend</Button>
          </Link>
        )}
        {b.status === "confirmed" && (
          <Link to={`/booking/${b.id}/manage?email=${encodeURIComponent(email.trim())}`}>
            <Button data-testid={`rental-manage-${i}`} size="sm" variant="outline" className="rounded-full font-ui h-8 text-xs">Manage</Button>
          </Link>
        )}
        {isPast && b.status === "completed" && (
          <Link to={`/receipt/${b.id}`} onClick={() => localStorage.setItem(`booking_email_${b.id}`, email.trim())}>
            <Button data-testid={`rental-receipt-${i}`} size="sm" variant="outline" className="rounded-full font-ui h-8 text-xs"><ReceiptText size={12} className="mr-1" /> Receipt</Button>
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="my-rentals-page" className="max-w-3xl mx-auto px-6 lg:px-10 pt-36 pb-24">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Your Account</p>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight">My <span className="italic gold-text">rentals</span></h1>

      {!data && (
        <div className="mt-8 bg-card border border-border rounded-2xl p-6 md:p-8">
          <p className="text-sm text-muted-foreground font-ui">Enter the email you've booked with to see your trips, receipts and loyalty progress.</p>
          <div className="mt-4 flex gap-3">
            <Input data-testid="my-rentals-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="input-dark" onKeyDown={(e) => e.key === "Enter" && load()} />
            <Button data-testid="my-rentals-load-button" onClick={() => load()} disabled={loading} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-6">
              {loading ? "Loading…" : "View rentals"}
            </Button>
          </div>
        </div>
      )}

      {data && (
        <div className="mt-8 space-y-8">
          <div data-testid="my-rentals-loyalty" className="rounded-2xl border border-primary/25 bg-card p-6 md:p-8 shadow-[0_8px_30px_rgba(224,36,52,0.06)]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-ui tracking-[0.25em] uppercase text-muted-foreground flex items-center gap-2"><Crown size={13} className="gold-text" /> Easy Drive Loyalty</p>
                <p data-testid="loyalty-tier" className="mt-1 font-display text-2xl md:text-3xl tracking-tight">{loyalty.tier}{loyalty.percent > 0 && <span className="text-primary"> — {loyalty.percent}% off every rental</span>}</p>
                <p className="mt-1 text-xs font-ui text-muted-foreground">{loyalty.member ? `Member ${loyalty.member_no} · ` : ""}{loyalty.rentals} completed/confirmed rental{loyalty.rentals === 1 ? "" : "s"}</p>
              </div>
              <Link to="/loyalty"><Button data-testid="loyalty-perks-button" variant="outline" className="rounded-full font-ui">See perks <ArrowRight size={14} className="ml-1.5" /></Button></Link>
            </div>
            {loyalty.next_tier && (
              <div className="mt-5">
                <div className="h-2 rounded-full bg-black/10 overflow-hidden">
                  <div data-testid="loyalty-progress-bar" className="h-full bg-primary rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                </div>
                <p data-testid="loyalty-next-tier" className="mt-2 text-xs font-ui text-muted-foreground">{loyalty.rentals_to_next} more rental{loyalty.rentals_to_next === 1 ? "" : "s"} to <b>{loyalty.next_tier}</b></p>
              </div>
            )}
          </div>

          {upcoming.length > 0 && (
            <section>
              <h2 className="font-display text-2xl tracking-tight flex items-center gap-2.5"><CalendarRange size={18} className="gold-text" /> Upcoming trips</h2>
              <div className="mt-4 space-y-3">{upcoming.map((b, i) => <RentalRow key={b.id} b={b} i={i} />)}</div>
            </section>
          )}

          <section>
            <h2 className="font-display text-2xl tracking-tight flex items-center gap-2.5"><History size={18} className="gold-text" /> {past.length ? "Past trips" : "No trips yet"}</h2>
            {past.length > 0 ? (
              <div className="mt-4 space-y-3">{past.map((b, i) => <RentalRow key={b.id} b={b} i={i + upcoming.length} past />)}</div>
            ) : (
              <div className="mt-4 rounded-2xl border border-dashed border-border p-8 text-center">
                <p className="text-sm font-ui text-muted-foreground">Nothing here yet — the island is waiting.</p>
                <Link to="/fleet"><Button data-testid="my-rentals-book-cta" className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-7">Browse the fleet <ArrowRight size={14} className="ml-2" /></Button></Link>
              </div>
            )}
          </section>

          {past.length > 0 && (
            <div className="text-center">
              <Link to="/fleet"><Button data-testid="my-rentals-book-again" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8 h-11">Book your next island ride <ArrowRight size={15} className="ml-2" /></Button></Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
